/* WHAT A REST PUTS BACK.
 *
 * Rests happen every session and nothing here modelled them, so the DM reset
 * hit points, exhaustion, drained maximums, resource counters and timed
 * conditions by hand, one character at a time, across two screens.
 *
 * The rules this encodes are the 2024 ones, and the places they are generous
 * are marked. What a rest must NOT do is as important as what it does: it is
 * the DM's call whether the party actually gets one, and a rest that quietly
 * cleared a condition the DM had set for story reasons would be worse than no
 * rest button at all.
 *
 * Pure, because every one of these decisions is worth a test:
 *   - a long rest restores hit points and ends exhaustion by ONE, not all
 *   - a drained maximum comes back on a long rest (that is what the drain is)
 *   - temporary hit points do NOT survive a rest of either length
 *   - death saves are cleared by any rest a conscious character takes
 *   - a condition with a countdown ends; a condition without one is the DM's
 *     and is left exactly where it is
 *   - concentration ends: you cannot rest and concentrate
 */
import { restoreMaxHp } from "./ddbLive";
import type { Character, Condition } from "./types";

export type RestKind = "short" | "long";

export interface Resource { id: string; name: string; current: number; max: number; recharge?: RestKind }
export interface RestChange { character: Character; changes: Partial<Character>; notes: string[] }

const asList = <T,>(v: unknown): T[] => (Array.isArray(v) ? (v as T[]) : []);
const isTimed = (c: string | Condition) => typeof c !== "string" && c.remaining !== undefined && c.remaining !== null;
const nameOf = (c: string | Condition) => (typeof c === "string" ? c : String(c.name || c.condition || ""));

/** A resource recharges on a short rest only when it says so; everything else
 *  comes back on a long one. Unmarked counters are the common case and the
 *  conservative reading. */
export const recharges = (r: Resource, kind: RestKind) => (kind === "long" ? true : r.recharge === "short");

/** What one character gets back, and the human-readable list of what changed. */
export function restCharacter(c: Character, kind: RestKind): RestChange {
  const changes: Partial<Character> = {};
  const notes: string[] = [];

  /* Hit points. A long rest is full; a short rest spends hit dice, which is a
     player decision with dice in it, so the console does not take it — standing
     rule 9. The short rest therefore restores nothing by itself. */
  if (kind === "long") {
    const was = Number(c.hp || 0);
    const max = Number(c.maxHp || 0);
    const restored = restoreMaxHp(c);
    const fullMax = Number(restored.maxHp ?? max);
    if (Object.keys(restored).length) { Object.assign(changes, restored); notes.push(`maximum back to ${fullMax}`); }
    if (was !== fullMax) { changes.hp = fullMax; notes.push(`${was} → ${fullMax} hit points`); }
  }

  /* Temporary hit points last until a rest of either length, by the rules. */
  if (Number(c.tempHp || 0) > 0) { changes.tempHp = 0; notes.push(`${c.tempHp} temporary hit points gone`); }

  /* Exhaustion: one level per long rest, not all of them. */
  if (kind === "long" && Number(c.exhaustion || 0) > 0) {
    changes.exhaustion = Number(c.exhaustion) - 1;
    notes.push(`exhaustion ${c.exhaustion} → ${changes.exhaustion}`);
  }

  /* Death saves belong to a dying character; anyone who rested is not dying. */
  if (Number(c.deathSuccesses || 0) || Number(c.deathFailures || 0)) {
    changes.deathSuccesses = 0; changes.deathFailures = 0; notes.push("death saves cleared");
  }

  /* Conditions with a countdown have run out by the end of a rest. One without a
     countdown is the DM's own mark — a curse, a disease, a story state — and a
     rest is not allowed to decide it has ended. */
  const conditions = asList<string | Condition>(c.conditions);
  const kept = conditions.filter((x) => !isTimed(x));
  if (kept.length !== conditions.length) {
    changes.conditions = kept;
    notes.push(`${conditions.filter(isTimed).map(nameOf).join(", ")} wore off`);
  }

  /* You cannot rest and concentrate. Both the field and any aura riding on it. */
  if (c.concentrationOn) { changes.concentrationOn = undefined; notes.push(`concentration on ${c.concentrationOn} ends`); }
  type ActiveAuraish = NonNullable<Character["auras"]>[number];
  const auras = asList<ActiveAuraish>(c.auras);
  const keptAuras = auras.filter((a) => !a.concentration && kind === "short");
  if (auras.length && keptAuras.length !== auras.length) {
    changes.auras = kind === "long" ? [] : keptAuras;
    const ended = auras.filter((a) => !keptAuras.includes(a)).map((a) => a.name);
    if (ended.length) notes.push(`${ended.join(", ")} ends`);
  }
  /* A light burns on through a short rest and is out after a long one: an hour
     of torch does not survive eight. */
  if (kind === "long" && c.light) { changes.light = undefined; notes.push(`${(c.light as { name?: string }).name || "their light"} is out`); }

  /* Counters. */
  const resources = asList<Resource>(c.resources);
  const back = resources.filter((r) => recharges(r, kind) && Number(r.current) < Number(r.max));
  if (back.length) {
    changes.resources = resources.map((r) => (recharges(r, kind) ? { ...r, current: r.max } : r));
    notes.push(`${back.map((r) => r.name).join(", ")} recharged`);
  }

  return { character: c, changes, notes };
}

/** The whole party, with the ones that changed first. */
export function restParty(party: Character[], kind: RestKind): RestChange[] {
  return party.map((c) => restCharacter(c, kind)).filter((r) => Object.keys(r.changes).length > 0);
}

export const restLabel = (kind: RestKind) => (kind === "long" ? "Long rest" : "Short rest");
